/**
 * Customer Google/Apple OIDC navigation helpers.
 *
 * SSOT: top-level full-document redirect to the IdP authorize URL (common
 * OAuth/OIDC pattern). Popup start was removed — operators expect the main
 * tab to leave for Google/Apple and return via the API callback land URL.
 *
 * Residual `handoffOidcPopupToOpener` / opener resume remain so an accidental
 * leftover named child window (old tab) can still hand land URL back safely.
 */

export const OIDC_POPUP_WINDOW_NAME = 'rpapp_customer_oidc';

export const OIDC_POPUP_MESSAGE_TYPE = 'rpapp:customer-oidc-popup' as const;

export interface OidcPopupCompleteMessage {
  readonly type: typeof OIDC_POPUP_MESSAGE_TYPE;
  readonly ok: boolean;
  readonly href: string;
}

/**
 * @deprecated Popup OIDC start is retired — always returns null so callers
 * fall through to top-level {@link navigateOidcAuthorizationUrl}.
 */
export function openOidcLoginPopup(): Window | null {
  return null;
}

export function closeOidcLoginPopup(popup: Window | null | undefined): void {
  if (popup === null || popup === undefined) {
    return;
  }
  try {
    if (!popup.closed) {
      popup.close();
    }
  } catch {
    // Cross-origin close can throw after IdP navigation — ignore.
  }
}

/**
 * Full-document assign to the OAuth authorize URL (same-window redirect).
 * The `popup` argument is ignored (kept for call-site compatibility).
 */
export function navigateOidcAuthorizationUrl(
  authorizationUrl: string,
  popup?: Window | null,
): void {
  closeOidcLoginPopup(popup);
  window.location.assign(authorizationUrl);
}

export function isOidcPopupChildWindow(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  if (window.name !== OIDC_POPUP_WINDOW_NAME) {
    return false;
  }
  try {
    return window.opener !== null && !window.opener.closed;
  } catch {
    return false;
  }
}

export function isOidcPopupHandoffUrl(href: string): boolean {
  let url: URL;
  try {
    url = new URL(href);
  } catch {
    return false;
  }
  if (url.origin !== window.location.origin) {
    return false;
  }
  const params = url.searchParams;
  if (params.has('oidcLand')) {
    return true;
  }
  if (params.has('oidcOfferPhone')) {
    return true;
  }
  // IdP / BE error bounce onto guest auth.
  if (params.has('error') || params.has('oidcError')) {
    const path = url.pathname;
    if (
      path === '/sign-in' ||
      path.endsWith('/sign-in') ||
      path === '/sign-up' ||
      path.endsWith('/sign-up')
    ) {
      return true;
    }
  }
  return false;
}

export function parseOidcPopupMessage(
  data: unknown,
): OidcPopupCompleteMessage | null {
  if (data === null || typeof data !== 'object') {
    return null;
  }
  const record = data as Record<string, unknown>;
  if (record['type'] !== OIDC_POPUP_MESSAGE_TYPE) {
    return null;
  }
  if (typeof record['href'] !== 'string' || record['href'].length === 0) {
    return null;
  }
  if (typeof record['ok'] !== 'boolean') {
    return null;
  }
  return {
    type: OIDC_POPUP_MESSAGE_TYPE,
    ok: record['ok'],
    href: record['href'],
  };
}

/** Residual named child → opener: post land/error URL then close. */
export function handoffOidcPopupToOpener(href: string, ok = true): boolean {
  if (!isOidcPopupChildWindow()) {
    return false;
  }
  if (!isOidcPopupHandoffUrl(href)) {
    return false;
  }
  try {
    const message: OidcPopupCompleteMessage = {
      type: OIDC_POPUP_MESSAGE_TYPE,
      ok,
      href,
    };
    window.opener.postMessage(message, window.location.origin);
  } catch {
    return false;
  }
  try {
    window.close();
  } catch {
    // Ignore — opener still received the message.
  }
  return true;
}

export function subscribeOidcPopupComplete(
  onComplete: (message: OidcPopupCompleteMessage) => void,
): () => void {
  const handler = (event: MessageEvent): void => {
    if (event.origin !== window.location.origin) {
      return;
    }
    const parsed = parseOidcPopupMessage(event.data);
    if (parsed === null) {
      return;
    }
    onComplete(parsed);
  };
  window.addEventListener('message', handler);
  return () => {
    window.removeEventListener('message', handler);
  };
}
